import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { Modal } from "../";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import {
  useCreateExtraSuboption,
  useUpdateExtraSuboption,
} from "../../../hooks/useProducts";

const CreateSuboptionModal = ({
  isOpen,
  onClose,
  businessId,
  extraId,
  optionId,
  suboptionToEdit = null,
}) => {
  const toast = useToast();
  const createMutation = useCreateExtraSuboption();
  const updateMutation = useUpdateExtraSuboption();
  const isEditMode = !!suboptionToEdit;

  const [formData, setFormData] = useState({
    name: "",
    price: "",
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when suboptionToEdit changes
  useEffect(() => {
    if (suboptionToEdit) {
      setFormData({
        name: suboptionToEdit.name || "",
        price: suboptionToEdit.price?.toString() || "",
      });
    } else {
      setFormData({
        name: "",
        price: "",
      });
    }
    setErrors({});
  }, [suboptionToEdit, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y punto decimal
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      // Limitar a 15 caracteres
      const limitedValue = value.slice(0, 15);
      setFormData((prev) => ({ ...prev, price: limitedValue }));
      if (errors.price) {
        setErrors((prev) => ({ ...prev, price: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.price?.trim()) {
      newErrors.price = "Price is required";
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = "Price must be a valid positive number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      if (isEditMode) {
        const payload = {
          id: suboptionToEdit.id,
          name: formData.name?.trim(),
          price: formData.price?.trim(),
        };
        await updateMutation.mutateAsync({
          businessId,
          extraId,
          optionId,
          suboptionId: suboptionToEdit.id,
          payload,
        });
        handleApiSuccess("Suboption updated successfully", toast);
      } else {
        const payload = {
          name: formData.name?.trim(),
          price: formData.price?.trim(),
          enabled: true,
        };
        await createMutation.mutateAsync({
          businessId,
          extraId,
          optionId,
          payload,
        });
        handleApiSuccess("Suboption created successfully", toast);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: isEditMode
          ? "Failed to Update Suboption"
          : "Failed to Create Suboption",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      price: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Suboption" : "Create Suboption"}
      subtitle={
        isEditMode
          ? "Update suboption details"
          : "Add a new suboption to this option"
      }
      maxWidth="max-w-md"
      className="ordering-catalog-surface"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-white">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Suboption name"
            required
          />
          {errors.name && (
            <div className="text-destructive mt-1 text-xs">{errors.name}</div>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-white">
            Price <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handlePriceChange}
            maxLength={15}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="0"
            required
          />
          {errors.price && (
            <div className="text-destructive mt-1 text-xs">{errors.price}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.name.trim() ||
              !formData.price.trim()
            }
            className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditMode
              ? updateMutation.isPending
                ? "Updating..."
                : "Update Suboption"
              : createMutation.isPending
                ? "Creating..."
                : "Create Suboption"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSuboptionModal;
